import { useEffect, useState } from 'react'
import { CalendarDays, ChevronRight, ClipboardList, ImageIcon, MapPin, Star } from 'lucide-react'
import { Shell, useShellMeta } from '../admin/Shell.js'
import { Link } from '../router.js'
import { Trail } from '../navigation/Trail.js'
import { apiGet } from '../admin/primitives.js'
import { LiveNumber, QuietNumber } from '../soirees/lib.js'
import { estErreurApi, fraicheurFr, nombreFr, PERIODES, type CommunicationPeriod } from './lib.js'
import { useMenuLien } from '../layout/menu-tuiles.js'

/**
 * COMMUNICATION V2 — le hub PAGE GOOGLE (§54-57).
 *
 * « Page Google » est le MODULE MÉTIER ; « Google Business Profile » reste
 * le nom du connecteur dans Administration → Intégrations. Résumé RÉEL
 * (impressions, Maps/Recherche, actions clients, note) puis tuiles
 * fonctions — Avis disponible, Événements/Photos honnêtement À venir
 * (capacités provider auditées : GOOGLE_EVENT_POST existe côté portail
 * d'écriture mais aucun module n'est développé).
 */

interface Delta { current: number | null; previous: number | null; state: string }
interface VueGoogle {
  connected: boolean
  health: string
  locationTitle: string | null
  impressions: Delta | null
  maps: Delta | null
  search: Delta | null
  actions: { key: string; label: string; delta: Delta }[]
  reviews: { average_rating: number | null; total: number; to_answer: number } | null
  lastSyncAt: string | null
}

function useGoogle(periode: CommunicationPeriod): { data: VueGoogle | null; erreur: string | null } {
  const [data, setData] = useState<VueGoogle | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  useEffect(() => {
    void apiGet<VueGoogle | { error: string; message?: string }>(`/api/v1/communication/google?period=${periode}`)
      .then((j) => { if (estErreurApi(j)) setErreur(j.message ?? 'Lecture impossible.'); else { setErreur(null); setData(j as VueGoogle) } })
      .catch(() => setErreur('Gestion n’a pas répondu.'))
  }, [periode])
  return { data, erreur }
}

export function GoogleHubPage() {
  const meta = useShellMeta()
  const [periode, setPeriode] = useState<CommunicationPeriod>('THIS_WEEK')
  const { data, erreur } = useGoogle(periode)
  const menuLien = useMenuLien()

  const fonctions = [
    {
      id: 'avis', label: 'Avis', Icon: Star, route: '/communication/avis', etat: null,
      description: 'La note officielle et les avis à traiter.',
      info: data?.reviews && data.reviews.average_rating !== null
        ? `${data.reviews.average_rating.toLocaleString('fr-FR')} / 5 · ${nombreFr(data.reviews.total)} avis${data.reviews.to_answer > 0 ? ` · ${data.reviews.to_answer} à traiter` : ''}`
        : null,
    },
    {
      id: 'publications', label: 'Publications', Icon: ClipboardList, route: '/communication/publications', etat: null,
      description: 'Composer et programmer les publications de la fiche.', info: null,
    },
    {
      id: 'evenements', label: 'Événements', Icon: CalendarDays, route: null, etat: 'À venir',
      description: 'Les événements publiés sur la fiche Google.', info: null,
    },
    {
      id: 'photos', label: 'Photos', Icon: ImageIcon, route: null, etat: 'À venir',
      description: 'Les photos de la fiche Google.', info: null,
    },
  ]

  return (
    <Shell active="communication" meta={meta}>
      <main className="lc-page lc-stagger cm-page">
        <Trail />
        <header className="cm-head">
          <div>
            <h1 className="lc-title">Page Google</h1>
            <p className="lc-subtitle">
              {data?.locationTitle ? <>{data.locationTitle} · </> : null}
              {data?.connected
                ? <span className="lc-pill lc-pill--ok ig-badge"><span className="lc-dot lc-dot--ok lc-dot--live" />Fiche connectée</span>
                : <span className="lc-pill lc-pill--idle ig-badge">Fiche non connectée</span>}
              {data?.lastSyncAt ? <span className="lc-hint"> mis à jour {fraicheurFr(data.lastSyncAt)}</span> : null}
            </p>
            {data && !data.connected ? (
              <p className="lc-hint">
                Visibilité et actions clients attendent la connexion de la fiche —
                configurable dans <Link href="/communication/integrations/google">Administration → Intégrations → Google Business Profile</Link>.
              </p>
            ) : null}
          </div>
          <div className="cm-head__actions">
            <div className="lc-seg cm-periods" role="group" aria-label="Période">
              {PERIODES.map((p) => (
                <button key={p.value} type="button"
                  className={`lc-seg__btn ${periode === p.value ? 'lc-seg__btn--on' : ''}`}
                  aria-pressed={periode === p.value}
                  onClick={() => setPeriode(p.value)}>{p.label}</button>
              ))}
            </div>
          </div>
        </header>
        {erreur ? <p className="lc-hint">{erreur}</p> : null}

        {data?.connected ? (
          <div className="gh-kpis lc-stagger">
            <div className="lc-card cm-widget gh-kpi lc-in">
              <strong className="cm-big__num">
                {data.impressions?.current !== null && data.impressions
                  ? <LiveNumber value={data.impressions.current} format={nombreFr} />
                  : <span className="ig-na">—</span>}
              </strong>
              <span className="cm-big__label">impressions</span>
            </div>
            <div className="lc-card cm-widget gh-kpi lc-in">
              <strong className="gh-kpi__val">{data.maps?.current !== null && data.maps ? <QuietNumber value={data.maps.current} format={nombreFr} /> : '—'}</strong>
              <span className="cm-big__label">via Maps</span>
            </div>
            <div className="lc-card cm-widget gh-kpi lc-in">
              <strong className="gh-kpi__val">{data.search?.current !== null && data.search ? <QuietNumber value={data.search.current} format={nombreFr} /> : '—'}</strong>
              <span className="cm-big__label">via Recherche</span>
            </div>
            {data.actions.map((a) => (
              <div key={a.key} className="lc-card cm-widget gh-kpi lc-in">
                <strong className="gh-kpi__val">{a.delta.current !== null ? <QuietNumber value={a.delta.current} format={nombreFr} /> : '—'}</strong>
                <span className="cm-big__label">{a.label.toLowerCase()}</span>
              </div>
            ))}
          </div>
        ) : null}

        <div className="fh-grid">
          {fonctions.map((f) => {
            const corps = (
              <>
                <span className="lc-card__edge" aria-hidden="true" />
                <span className="lc-card__icon" aria-hidden="true"><f.Icon /></span>
                <div className="fh-tile__texte">
                  <h2>{f.label}</h2>
                  <p className="lc-hint">{f.description}</p>
                  {f.info ? <span className="fh-tile__provider">{f.info}</span> : null}
                </div>
                <div className="fh-tile__pied">
                  {f.etat ? <span className="lc-pill lc-pill--idle fh-tile__avenir">{f.etat}</span> : null}
                  {f.route ? <span className="fh-tile__cta"><ChevronRight size={16} aria-hidden="true" /></span> : null}
                </div>
              </>
            )
            if (!f.route) return <div key={f.id} className="lc-card fh-tile fh-tile--muette lc-in">{corps}</div>
            return <Link key={f.id} href={f.route} className="lc-card fh-tile lc-in" {...menuLien(f.route, f.label)}>{corps}</Link>
          })}
        </div>

        <p className="lc-hint cm-roadnote">
          <MapPin size={12} aria-hidden="true" /> La configuration du connecteur
          (OAuth, fiche, diagnostic) vit dans Administration → Intégrations —
          cette page CONSOMME la fiche, elle ne la configure pas.
        </p>
      </main>
    </Shell>
  )
}
